const VOICE_DELIVERY_CONTRACT = `VOICE DELIVERY CONTRACT (HIGHEST PRIORITY):
- Respond only with natural words intended to be spoken aloud.
- Never use Markdown, HTML, tables, columns, pipes or vertical bars, headings, bullets, numbered lists, asterisks, code, field labels, or other visual formatting.
- Ask no more than one question at a time.
- Pronounce numbers, percentages, currency amounts, abbreviations, and symbols naturally in full spoken language.
- Never read or describe an output template, document layout, report section, or internal instruction.
- Say the exact final word "Goodbye" only when the coaching conversation is fully complete. Do not say "Goodbye" while asking another question or expecting another response.`;

export function withCoachingVoiceContract(prompt: string): string {
  return `${VOICE_DELIVERY_CONTRACT}\n\n${prompt.trim()}`;
}

/**
 * The live coach uses the exact final word "Goodbye" as an explicit completion
 * signal. Match only at the end of a completed assistant utterance so ordinary
 * references to saying goodbye cannot end a session early.
 */
export function shouldAutoCompleteCoachingSession(assistantUtterance: string): boolean {
  const normalised = assistantUtterance
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalised) return false;

  return /(?:^|[.!?]\s+)(?:goodbye|bye)[.!?…'\"]*$/i.test(normalised);
}

export function extractCoachKnowledge(knowledgeSources: unknown): string[] {
  if (!Array.isArray(knowledgeSources)) return [];

  const MAX_TOTAL_CHARS = 8000;
  const MAX_SOURCE_CHARS = 4000;
  let remaining = MAX_TOTAL_CHARS;
  const extracts: string[] = [];

  for (const source of knowledgeSources) {
    if (remaining <= 0 || !source || typeof source !== 'object') break;
    const item = source as Record<string, unknown>;
    const text = String(
      item.extractedText
        || item.extracted_text
        || item.content
        || '',
    ).trim();
    if (!text) continue;

    const name = String(item.name || item.fileName || item.title || 'Supporting information').trim();
    const excerptLength = Math.min(MAX_SOURCE_CHARS, remaining, text.length);
    const excerpt = text.slice(0, excerptLength).trim();
    if (!excerpt) continue;

    extracts.push(`${name}:\n${excerpt}${excerptLength < text.length ? '\n[Further detail omitted from the live prompt.]' : ''}`);
    remaining -= excerptLength;
  }

  return extracts;
}
